import { 
  RegistrationStatus, 
  MealPreference, 
  ApiResponse, 
  EventRegistration 
} from "./shared.types";

export interface CreateRegistrationDTO {
  eventId: string;
  userEmail: string;
  userName: string;
  userId?: string;
  employeeId?: string;
  sector?: string;
  cluster?: string;
  mealPreference?: MealPreference;
  status?: RegistrationStatus;
  notes?: string;
  guestCount?: number;
  additionalAdults?: number;
  additionalChildren?: number;
}

export interface UpdateRegistrationDTO {
  mealPreference?: MealPreference;
  status?: RegistrationStatus;
  userName?: string;
  employeeId?: string;
  sector?: string;
  cluster?: string;
  notes?: string;
  guestCount?: number;
  additionalAdults?: number;
  additionalChildren?: number;
}

export interface CheckInDTO {
  registrationId: string;
  eventId: string;
  checkedInBy?: string;
}

export interface RegistrationFormData {
  userName: string;
  userEmail: string;
  employeeId?: string;
  sector?: string;
  cluster?: string;
  mealPreference: MealPreference;
  status: RegistrationStatus;
  notes?: string;
  guestCount?: number;
  additionalAdults?: number;
  additionalChildren?: number;
}

export interface RegistrationStats {
  totalRegistrations: number;
  confirmedCount: number;
  tentativeCount: number;
  declinedCount: number;
  cancelledCount: number;
  attendedCount: number;
  mealStats: {
    none: number;
    meat: number;
    vegetarian: number;
    vegan: number;
    flex: number;
    noMeal: number;
  };
  capacityRemaining: number;
  totalParticipants: number;
  totalAdults: number;
  totalChildren: number;
}

export interface RegistrationResponse extends ApiResponse<EventRegistration> {}

export interface RegistrationListResponse extends ApiResponse<EventRegistration[]> {
  count?: number;
}

export interface CheckInResponse {
  success: boolean;
  registration?: EventRegistration;
  message?: string;
  error?: string;
  alreadyCheckedIn?: boolean;
}

export interface RegistrationStatsResponse extends ApiResponse<RegistrationStats> {}


export const validateRegistrationForm = (
  data: RegistrationFormData,
  mealOptionsEnabled: boolean,
): string[] => {
  const errors: string[] = [];

  if (!data.userName || data.userName.trim().length < 2) {
    errors.push("Name must be at least 2 characters");
  }

  if (
    mealOptionsEnabled &&
    data.status === RegistrationStatus.Registered &&
    data.mealPreference === MealPreference.None
  ) {
    errors.push("Please select a meal preference");
  }

  return errors;
};

export const isRegistrationActive = (
  registration: EventRegistration,
): boolean => {
  return (
    registration.status === RegistrationStatus.Registered ||
    registration.status === RegistrationStatus.Tentative
  );
};

export const canCheckIn = (registration: EventRegistration): boolean => {
  return (
    registration.status === RegistrationStatus.Registered &&
    !registration.checkInTime
  );
};

export const formatRegistrationDate = (isoDate: string): string => {
  return new Date(isoDate).toLocaleDateString("de-DE", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const getMealPreferenceLabel = (pref: MealPreference): string => {
  switch (pref) {
    case MealPreference.Meat:
      return "Meat";
    case MealPreference.Vegetarian:
      return "Vegetarian";
    case MealPreference.Vegan:
      return "Vegan";
    case MealPreference.Flex:
      return "Flex";
    case MealPreference.NoMeal:
      return "No Meal Required";
    case MealPreference.None:
    default:
      return "No preference";
  }
};

export const getMealPreferenceOptions = () => [
  { value: MealPreference.Meat, label: "Meat" },
  { value: MealPreference.Vegetarian, label: "Vegetarian" },
  { value: MealPreference.Vegan, label: "Vegan" },
  { value: MealPreference.Flex, label: "Flex" },
  { value: MealPreference.NoMeal, label: "No Meal Required" },
];


export {
  getStatusLabel,
  getStatusColor,
  getResponseOptions,
  getStatusIcon,
  getStatusClass,
  getSubmitButtonText,
} from "../components/utils/statusUtils";